import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { logout, setTokens } from '../store/slices/authSlice';
import {
  REQUEST_HALT_ERROR_CODE,
  REQUEST_HALT_MESSAGE,
  haltRequests,
  isRequestHalted,
  shouldHaltForStatus,
} from '../utils/requestHalt';
import { acceptLanguage } from '../i18n';

const baseUrl = process.env.REACT_APP_API_BASE_URL || '/api';

const normalizeToken = (token: unknown): string | null => {
  if (typeof token !== 'string') return null;
  const trimmed = token.trim();
  if (!trimmed || trimmed === 'undefined' || trimmed === 'null') return null;
  return trimmed;
};

const baseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers) => {
    const token = normalizeToken(localStorage.getItem('accessToken'));
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    // The backend localizes its responses by this header (RFC_I18N R10).
    headers.set('Accept-Language', acceptLanguage());
    return headers;
  },
});

export const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const skipHalt = !!(extraOptions as Record<string, unknown> | undefined)?.skipHalt;
  if (!skipHalt && isRequestHalted()) {
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error: REQUEST_HALT_ERROR_CODE,
        data: {
          errorCode: REQUEST_HALT_ERROR_CODE,
          message: REQUEST_HALT_MESSAGE,
        },
      } as FetchBaseQueryError,
    };
  }

  let result = await baseQuery(args, api, extraOptions);

  if (result.error && shouldHaltForStatus(result.error.status)) {
    haltRequests({
      status: result.error.status,
      message: (result.error.data as { message?: string } | undefined)?.message,
      source: 'rtk-query',
    });
    return result;
  }

  if (result.error && result.error.status === 401) {
    const refreshToken = normalizeToken(localStorage.getItem('refreshToken'));
    if (!refreshToken) {
      api.dispatch(logout());
      return result;
    }

    const refreshResult = await baseQuery(
      { url: '/auth/refresh', method: 'POST', body: { refreshToken } },
      api,
      extraOptions
    );

    if (refreshResult.data && (refreshResult.data as any).accessToken) {
      const refreshData = refreshResult.data as { accessToken?: string; refreshToken?: string };
      const tokens = {
        accessToken: normalizeToken(refreshData.accessToken),
        refreshToken: normalizeToken(refreshData.refreshToken) || refreshToken,
      };

      if (!tokens.accessToken) {
        api.dispatch(logout());
        return result;
      }

      api.dispatch(setTokens(tokens));
      // retry original query with new token
      result = await baseQuery(args, api, extraOptions);

      if (result.error && shouldHaltForStatus(result.error.status)) {
        haltRequests({
          status: result.error.status,
          message: (result.error.data as { message?: string } | undefined)?.message,
          source: 'rtk-query',
        });
      }
    } else {
      api.dispatch(logout());
    }
  }

  // Handle 403 Forbidden - redirect to home
  if (result.error && result.error.status === 403) {
    window.location.href = '/';
  }

  return result;
};

export default baseQueryWithReauth;
